/**
 * Повторний вхід поверх сторінки (UX-30; E3a-06b) за умовами Security з тікета:
 * 1. Шар — лише на `401 session_expired` (природний сплив). Будь-який інший 401 і обрив SSE
 *    обробляються як раніше: чернетки геть, екран веде на `/login`.
 * 2. Чернетки вкладки стерто й на `session_expired` (`main.tsx`, `onUnauthorized`), а нових, поки
 *    шар відкритий, не пишемо (`suspendDrafts`): введене живе лише в пам'яті відкритої форми.
 * 3. Поки шар відкритий, вміст під ним ПРИХОВАНИЙ (`visibility: hidden` на `#root`, `inert`),
 *    а назва вкладки — без назви запису. Пам'ять форми при цьому ціла: вузли лишаються в DOM.
 * 4. Після входу — хто тепер у cookie, каже лише сервер (`GET /api/v1/me`):
 *    - інший користувач чи відповіді немає — повний перехід на стартову сторінку, вміст так і не
 *      показується (`sessionStorage` вкладки стирається, пам'ять сторінки зникає з документом);
 *    - той самий — нова сесія чернеток (`rotateDraftSession`), відкриті форми записують чернетку
 *      під нею (`setDraftUser`), дані перечитуються. Невдалий запит сам НЕ повторюється: людина
 *      натискає «Зберегти» ще раз (рішення PM 14.10).
 * 5. Інші вкладки: `session-expired` ховає вміст і там, `signed-in` знімає шар після тієї самої
 *    звірки (`platform/session-signal.ts`).
 *
 * Шар показується лише вкладці, яка знає, хто ввійшов (`/me` у кеші): інакше на екрані ще нічого
 * не було, і екран іде на вхід, як раніше. Код форми входу — лінивий чанк (`features/relogin`),
 * а тут лише стан: модуль бере перший екран.
 */
import type { QueryClient } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import { bindNext } from '../app/next-owner.ts';
import { loginHref, safeNextPath } from '../app/next-path.ts';
import { pageTitle, withTitleBadge } from '../brand/env.ts';
import { shell as t } from '../i18n/uk/shell.ts';
import { resetPersonPrefs } from '../prefs/prefs.ts';
import {
  clearAllDrafts,
  rotateDraftSession,
  setDraftUser,
  suspendDrafts,
} from '../ui/save/drafts.ts';
import { fetchMe, type Me, onUnauthorized, platformKeys } from './api.ts';
import { onSessionSignal, sendSessionSignal } from './session-signal.ts';
import { clearPersonData, isLeaving } from './sign-out.ts';

/** Атрибут `<html>`, поки шар відкритий: CSS ховає `#root` (styles/shell.css). */
export const EXPIRED_ATTR = 'data-session-expired';

interface Open {
  /** Хто був у вкладці до спливу: з ним звіряємо того, хто ввійшов. */
  userId: string;
  /** Що було у фокусі — туди фокус і повертається після входу тим самим користувачем. */
  focus: Element | null;
  title: string;
}

let open: Open | null = null;
/**
 * Шар хоч раз перехопив `session_expired` у цьому документі. Відтоді відповідь `session_expired`
 * не веде екран на `/login` (`isUnauthorized`), навіть поки після входу дані ще перечитуються.
 */
let handles = false;
let finishing: Promise<void> | null = null;
const listeners = new Set<() => void>();

/** Повний перехід без запису в історії; тести підміняють (jsdom не дає перевизначити `location`). */
let go = (url: string) => location.replace(url);

const emit = () => {
  for (const listener of listeners) listener();
};

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const reloginOpen = (): boolean => open !== null;
export const reloginHandles = (): boolean => handles;

/** Шар відкритий — оболонка показує його і не тримає потоку подій. */
export function useReloginOpen(): boolean {
  return useSyncExternalStore(subscribe, reloginOpen, () => false);
}

function root(): HTMLElement | null {
  return typeof document === 'undefined' ? null : document.getElementById('root');
}

function hideContent(): void {
  document.documentElement.setAttribute(EXPIRED_ATTR, '');
  const el = root();
  if (el) el.inert = true;
  document.title = pageTitle(t.reloginTitle);
}

function showContent(state: Open): void {
  document.documentElement.removeAttribute(EXPIRED_ATTR);
  const el = root();
  if (el) el.inert = false;
  // Збережений заголовок — з числом сповіщень, що діє зараз, а не з тим, що було до шару.
  document.title = withTitleBadge(state.title);
}

function knownUser(queryClient: QueryClient): string | undefined {
  return queryClient.getQueryData<Me>(platformKeys.me)?.id;
}

/** Сесія спливла: сховати вміст, призупинити чернетки, показати шар. `false` — шар не для цієї вкладки. */
function expire(queryClient: QueryClient, announce: boolean): boolean {
  if (open) return true;
  const userId = knownUser(queryClient);
  if (!userId) return false;
  handles = true;
  suspendDrafts(true);
  clearAllDrafts();
  open = { userId, focus: document.activeElement, title: document.title };
  hideContent();
  emit();
  if (announce) sendSessionSignal('session-expired');
  return true;
}

/** Інший користувач чи невідомо хто: повне перезавантаження до показу будь-чого (умова 4). */
function leaveForStart(queryClient: QueryClient): void {
  hideContent();
  void queryClient.cancelQueries();
  // Увійшла інша людина: поля попередньої в `cl:prefs` (пояс, стартова…) не переживають перехід.
  resetPersonPrefs();
  try {
    sessionStorage.clear();
  } catch {
    // Сховище недоступне — у ньому нічого й немає.
  }
  go('/');
}

async function verify(queryClient: QueryClient, state: Open): Promise<void> {
  let me: Me | null = null;
  try {
    me = await fetchMe();
  } catch {
    me = null;
  }
  if (open !== state) return;
  if (!me || me.id !== state.userId) {
    leaveForStart(queryClient);
    return;
  }
  // Той самий користувач: нова сесія чернеток, відкриті форми перепишуть свою чернетку під нею.
  rotateDraftSession();
  suspendDrafts(false);
  setDraftUser(me.id);
  queryClient.setQueryData(platformKeys.me, me);
  open = null;
  showContent(state);
  emit();
  if (state.focus instanceof HTMLElement && state.focus.isConnected) {
    state.focus.focus({ preventScroll: true });
  }
  // Дані перечитуються з новою сесією; помилки `session_expired` в екранах зникнуть із ними.
  void queryClient.invalidateQueries({ queryKey: ['p'] });
}

/**
 * Після входу (шар цієї вкладки чи сигнал іншої): звірити людину й зняти шар або піти на старт.
 * Повторний виклик під час звірки чекає ту саму.
 */
export function finishRelogin(queryClient: QueryClient): Promise<void> {
  const state = open;
  if (!state) return Promise.resolve();
  finishing ??= verify(queryClient, state).finally(() => {
    finishing = null;
  });
  return finishing;
}

/**
 * «Вийти» з шару: людина не продовжує роботу. Сесія вже не діє; чернеток немає. Повний перехід на
 * вхід без повернення сюди: того, хто ввійде наступним, ця сторінка не стосується.
 */
export async function leaveRelogin(): Promise<void> {
  hideContent();
  // Те саме прибирання, що в «Вийти» меню: за ПК може сісти інша людина (поля людини в `cl:prefs`,
  // недавні записи, останні команди палітри). Кеш запитів зникає разом із документом.
  await clearPersonData();
  go('/login');
}

/** Лінивий код шару не завантажився (реліз прибрав старий чанк) — вхід окремою сторінкою. */
export function reloginUnavailable(): void {
  hideContent();
  const here = location.pathname + location.search;
  // Повернутись сюди після входу — лише тій самій людині (app/next-owner.ts).
  bindNext(safeNextPath(here), open?.userId);
  go(loginHref(here));
}

/**
 * Хтось увійшов в іншій вкладці. Вкладка з шаром звіряє людину й знімає шар. Вкладка, що працює,
 * перевіряє, чи не змінилася людина в cookie: інший користувач — на старт, щоб дані першого не
 * лишились перед другим (той самий принцип, що в умові 4).
 */
async function signedInElsewhere(queryClient: QueryClient): Promise<void> {
  if (open) return finishRelogin(queryClient);
  const known = knownUser(queryClient);
  if (!known) return;
  let me: Me;
  try {
    me = await fetchMe();
  } catch {
    // Сесії цієї вкладки вже немає (401 обробить свій слухач) чи мережа — нічого не робимо.
    return;
  }
  if (me.id !== known) leaveForStart(queryClient);
}

/** Старт застосунку (`main.tsx`): `session_expired` від API і сигнали інших вкладок. */
export function watchRelogin(queryClient: QueryClient): () => void {
  const offApi = onUnauthorized((kind) => {
    // Під час «Вийти» шар повторного входу не відкривається: вкладка вже йде на вхід.
    if (kind === 'expired' && !isLeaving()) expire(queryClient, true);
  });
  const offSignal = onSessionSignal((type) => {
    if (type === 'session-expired') {
      // Сесія спільна: спливла й тут. Чернетки вкладки — геть, як на власну відповідь, навіть
      // якщо шар цій вкладці не потрібен (вона ще не знає, хто ввійшов).
      if (!expire(queryClient, false)) clearAllDrafts();
    } else {
      void signedInElsewhere(queryClient);
    }
  });
  return () => {
    offApi();
    offSignal();
  };
}

/** Лише для тестів. */
export function resetReloginForTests(navigate: (url: string) => void = () => undefined): void {
  if (open) showContent(open);
  // `leaveForStart` / `leaveRelogin` ставлять ознаку без `open`: тест не лишає її наступному.
  document.documentElement.removeAttribute(EXPIRED_ATTR);
  const el = root();
  if (el) el.inert = false;
  open = null;
  handles = false;
  finishing = null;
  suspendDrafts(false);
  go = navigate;
  emit();
}
